import React from 'react';
import { IconLoyalty } from '@noths/polaris-client-ribbons-design-system';

import type { ArrivesBy } from 'src/components/organisms/DeliveryBlock/types';
import type { DeliveryOption } from 'src/redux/delivery/types';
import { getCutoffMessage } from './utils/getCutoffMessage';
import { getDateNDaysFromNow } from './utils/getDateNDaysFromNow';
import { getDeliveryOptionText } from './utils/getDeliveryOptionText';
import { getFormattedDeliveryDate } from './utils/getFormattedDeliveryDate';
import { getFormattedPrice } from './utils/getFormattedPrice';
import * as styles from './DeliveryOptions.styles';

const deliveryOptionArrivesByDate = (option: DeliveryOption, date: string) => {
  if (option.exactDate && option.date) {
    return option.date <= date;
  } else if (option.max) {
    return getDateNDaysFromNow(option.max) <= date;
  }

  return false;
};

interface DeliveryOptionsProps {
  arrivesBy?: ArrivesBy;
  deliveryOptions: DeliveryOption[] | [];
  partnerName: string;
  specialSize?: boolean;
}

export const DeliveryOptions = ({
  arrivesBy,
  deliveryOptions,
  partnerName,
  specialSize = false,
}: DeliveryOptionsProps) => {
  const hasOptionThatArrivesByEvent = arrivesBy
    ? deliveryOptions.some((option) => deliveryOptionArrivesByDate(option, arrivesBy.date))
    : false;
  const hasFreeOption = deliveryOptions.some((option) => Number(option.price.amount) === 0);

  return (
    <>
      {hasOptionThatArrivesByEvent && (
        <strong css={styles.arrivesByLabel}>
          YOU&apos;LL GET IT BY <em>{arrivesBy!.name.toUpperCase()}</em>
        </strong>
      )}
      <ul css={styles.deliveryOptionsList}>
        {deliveryOptions.map((deliveryOption) => {
          const {
            cutoffDateTime,
            date,
            exactDate,
            max,
            min,
            optionCode,
            partnerFreeDeliveryThresholdPrice,
            preSalePrice,
            price: { amount, currencyCode },
          } = deliveryOption;
          const optionPrice = Number(amount);
          const cutoffMessage = getCutoffMessage(cutoffDateTime);
          const formattedPrice = getFormattedPrice(optionPrice, currencyCode);
          const showArrivesBy = arrivesBy
            ? deliveryOptionArrivesByDate(deliveryOption, arrivesBy.date)
            : false;

          return (
            <li css={styles.item} key={optionCode}>
              <div css={styles.optionInfo}>
                <span>
                  <h6 css={styles.optionHeading}>
                    {exactDate && date ? (
                      <b>
                        {optionCode === 'SATURDAY' ? 'Get it on ' : 'Estimated delivery '}
                        <time dateTime={date}>{getFormattedDeliveryDate(date)}</time>
                      </b>
                    ) : (
                      `${min}-${max} days`
                    )}
                    {showArrivesBy ? (
                      <span>
                        {' '}
                        · Arrives in time for{' '}
                        <time dateTime={arrivesBy!.date}>{arrivesBy!.name}</time>
                      </span>
                    ) : null}
                  </h6>
                  <p css={styles.optionBody}>
                    {cutoffMessage ? `Order by ${cutoffMessage} · ` : ''}
                    {specialSize ? 'Special size delivery' : getDeliveryOptionText(optionCode)}
                  </p>
                </span>
                <span css={styles.optionPrices}>
                  {preSalePrice && (
                    <span>
                      {getFormattedPrice(Number(preSalePrice.amount), preSalePrice.currencyCode)}
                    </span>
                  )}
                  <b css={styles.optionPrice} data-price={optionPrice}>
                    {formattedPrice}
                  </b>
                </span>
              </div>
              {partnerFreeDeliveryThresholdPrice && !hasFreeOption && (
                <p css={styles.freePartnerDelivery}>
                  <span>
                    <IconLoyalty color={'var(--functional-green-base)'} height={16} width={16} />
                  </span>
                  <span>
                    <b>Free standard delivery</b> when you spend over{' '}
                    {getFormattedPrice(
                      Number(partnerFreeDeliveryThresholdPrice.amount),
                      partnerFreeDeliveryThresholdPrice.currencyCode,
                    )}{' '}
                    with {partnerName}
                  </span>
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
};
